import * as z from 'zod/v4'

import {type CommandValueFor, createNamespacedActionCreator} from '../defineAction'

const createAction = createNamespacedActionCreator('note')

const open = createAction('open', z.strictObject({noteId: z.string()}))
const create = createAction(
  'create',
  z.strictObject({folderId: z.string().optional(), title: z.string().optional()}).optional()
)

const noteIdParam = z
  .string()
  .meta({description: 'Note to read. Omit to read the note the user currently has open (from context_entryPoint).'})
  .optional()

// note_read's client contract: the client only ever opens the note and returns its raw text, so
// this is the entire shape it needs. The much larger param set the model actually fills in lives
// in noteReadFilterParams below, kept off the wire by commands/index.ts's modelParamOverrides +
// projectClientArgs — the filter params are consumed server-side once the raw text comes back.
const read = createAction('read', z.strictObject({noteId: noteIdParam}).optional())

export const noteActions = [open, create, read] as const

// note_read's model-facing schema — everything above the client contract needs to describe a
// grep/window/token-cap read. Numeric params are intentionally unbounded here; the server clamps
// and defaults them, so an out-of-range value the model emits is corrected rather than rejected
// as a malformed command. Registered as note_read's tool-input override in commands/index.ts.
export const noteReadFilterParams = z.strictObject({
  noteId: noteIdParam,
  pattern: z
    .string()
    .meta({
      description:
        'Regular expression to search for. When set, only matching lines and their surrounding context are ' +
        'returned. Omit both pattern and startLine to return the whole note, capped by maxTokens.'
    })
    .optional(),
  ignoreCase: z.boolean().meta({description: 'Match the pattern case-insensitively. Defaults to true.'}).optional(),
  contextLines: z
    .number()
    .meta({
      description:
        'Lines of context to include on each side of a pattern match. linesBefore/linesAfter override it per side.'
    })
    .optional(),
  linesBefore: z
    .number()
    .meta({description: 'Lines of context before each match. Overrides contextLines on that side.'})
    .optional(),
  linesAfter: z
    .number()
    .meta({description: 'Lines of context after each match. Overrides contextLines on that side.'})
    .optional(),
  maxMatches: z
    .number()
    .meta({description: 'Maximum number of pattern matches to return, in document order.'})
    .optional(),
  startLine: z
    .number()
    .meta({
      description:
        '1-based line to start reading from. With endLine, returns that exact line range instead of pattern-searching.'
    })
    .optional(),
  endLine: z
    .number()
    .meta({description: '1-based line to stop reading at, inclusive. Defaults to the end of the note.'})
    .optional(),
  maxTokens: z
    .number()
    .meta({
      description: 'Hard cap on the returned text size. Output is truncated to fit and marked as truncated when it is.'
    })
    .optional()
})

export const noteModelParamOverrides = {
  note_read: noteReadFilterParams
} satisfies Partial<Record<CommandValueFor<typeof noteActions>, z.ZodType>>

export const noteDescriptions = {
  note_open: 'Open an existing note by its id.',
  note_create: 'Create a new note, optionally placed in a folder and given a title.',
  note_read:
    "Read the actual text of one of the user's notes straight from their device. It is the fallback for " +
    "server_read-note: use it only when server_read-note says a note has no readable text yet, can't be read " +
    'right now, or is too large. Three ways to call it: pass a `pattern` to get matching lines with surrounding context (like ' +
    'grep); pass `startLine`/`endLine` to read an explicit line range; or pass neither to read the whole note. ' +
    'Output is line-numbered and always bounded by `maxTokens`. Reads the currently-open note by default; pass a ' +
    "different `noteId` to read another of the user's notes."
} satisfies Record<CommandValueFor<typeof noteActions>, string>
